import React, { useEffect, useState } from "react";
import {
  Button,
  Chip,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

const FIELD_SX = {
  "& .MuiInputBase-root": {
    height: 40,
  },
};

export default function AuditFilters({
  filters,
  setFilters,
  studentOptions,
  studentSearchLoading,
  onStudentSearch,
  onApply,
  onClear,
}) {
  const [studentInput, setStudentInput] = useState(filters.studentLabel || "");
  const [filterMode, setFilterMode] = useState("user");

  useEffect(() => {
    if (!filters.studentId) {
      setStudentInput("");
    }
  }, [filters.studentId]);

  const handleFilterModeChange = (_, newMode) => {
    if (!newMode) return;
    setFilterMode(newMode);
    setFilters((prev) => ({
      ...prev,
      studentId: "",
      studentLabel: "",
      email: "",
    }));
    setStudentInput("");
  };

  // Match by name, MT ref, or id number against the loaded student list.
  const filterStudents = (options, state) => {
    const q = state.inputValue.trim().toLowerCase();
    if (!q) return [];
    return options
      .filter(
        (o) =>
          String(o.label || "").toLowerCase().includes(q) ||
          String(o.mtRef || "").toLowerCase().includes(q) ||
          String(o.id ?? "").includes(q)
      )
      .slice(0, 50);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Filter audit records by student or by user.
      </Typography>

      <ToggleButtonGroup
        value={filterMode}
        exclusive
        onChange={handleFilterModeChange}
        size="small"
      >
        <ToggleButton value="user">By User</ToggleButton>
        <ToggleButton value="student">By Student</ToggleButton>
      </ToggleButtonGroup>

      {filterMode === "student" ? (
        <>
          <Autocomplete
            options={studentOptions}
            loading={studentSearchLoading}
            filterOptions={filterStudents}
            noOptionsText={
              studentInput.trim()
                ? "No matching students"
                : "Type a name, MT ref or student ID"
            }
            value={
              filters.studentId
                ? studentOptions.find((s) => String(s.id) === String(filters.studentId)) || {
                    id: filters.studentId,
                    label: filters.studentLabel || `Student ${filters.studentId}`,
                    mtRef: "",
                  }
                : null
            }
            inputValue={studentInput}
            onInputChange={(_, value, reason) => {
              setStudentInput(value);

              if (reason === "input") {
                onStudentSearch(value);
              }

              if (reason === "clear") {
                setFilters((prev) => ({
                  ...prev,
                  studentId: "",
                  studentLabel: "",
                }));
              }
            }}
            onChange={(_, value) => {
              setFilters((prev) => ({
                ...prev,
                studentId: value?.id || "",
                studentLabel: value
                  ? [value.label, value.mtRef].filter(Boolean).join(" - ")
                  : "",
              }));
            }}
            getOptionLabel={(option) =>
              [option?.label, option?.mtRef, option?.id ? `#${option.id}` : ""]
                .filter(Boolean)
                .join(" - ")
            }
            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
            sx={{
              maxWidth: 520,
              "& .MuiInputBase-root": {
                minHeight: 40,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search student"
                placeholder="Name, MT ref or student ID"
                size="small"
                fullWidth
              />
            )}
          />

          {filters.studentId && (
            <Chip
              label={`Student: ${filters.studentLabel}`}
              onDelete={() =>
                setFilters((prev) => ({
                  ...prev,
                  studentId: "",
                  studentLabel: "",
                }))
              }
              sx={{ width: "fit-content" }}
            />
          )}
        </>
      ) : (
        <TextField
          label="Filter by username"
          placeholder="e.g. alex.brown@bliss.com"
          value={filters.email}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              email: e.target.value,
            }))
          }
          size="small"
          sx={{ ...FIELD_SX, maxWidth: 420 }}
        />
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap flexWrap="wrap">
        <TextField
          label="Start date"
          type="date"
          value={filters.startDate || ""}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              startDate: e.target.value,
            }))
          }
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ ...FIELD_SX, minWidth: 170 }}
        />

        <TextField
          label="End date"
          type="date"
          value={filters.endDate || ""}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              endDate: e.target.value,
            }))
          }
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ ...FIELD_SX, minWidth: 170 }}
        />
      </Stack>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={onApply} sx={{ height: 40 }}>
          Apply filters
        </Button>
        <Button onClick={onClear} sx={{ height: 40 }}>
          Clear filters
        </Button>
      </Stack>
    </Stack>
  );
}